import { reportDarkPalette } from "assets/jss/material-dashboard-react.jsx";
import { getAppThemeMode } from "theme/appTheme.js";

/**
 * Unified “report night” surfaces for device/asset pages.
 * Light values preserve the existing blue-tinted product UI.
 */
export const getAppShellUi = isDark => {
  if (!isDark) {
    return {
      pageBg:
        "radial-gradient(circle at top left, rgba(33,150,243,0.12), transparent 32%), linear-gradient(180deg, #f6f8fb 0%, #eef3f8 100%)",
      surface: "rgba(255, 255, 255, 0.88)",
      surfaceSolid: "#ffffff",
      surfaceRaised: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, #f8fbff 100%)",
      surfaceInset: "#f8fafc",
      surfaceMuted: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
      headerGradient:
        "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(244,247,251,0.9) 100%)",
      border: "rgba(148, 163, 184, 0.18)",
      borderStrong: "rgba(148, 163, 184, 0.28)",
      text: "#0f172a",
      textSecondary: "#334155",
      textMuted: "#64748b",
      accent: "#2563eb",
      accentSoft: "rgba(37, 99, 235, 0.1)",
      accentGlow: "0 18px 34px rgba(37, 99, 235, 0.22)",
      tabRail: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
      tabIndicator: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      tabSelectedText: "#ffffff",
      tabIdleText: "#1e293b",
      jsonKey: "#0057ff",
      jsonValue: "#c026d3",
      shadowCard: "0 18px 42px rgba(15, 23, 42, 0.1)",
      shadowInset: "inset 0 0 0 1px rgba(148, 163, 184, 0.14)",
      weatherPanel:
        "linear-gradient(180deg, #ffffff 0%, #edf7ff 100%)",
      pillBg: "rgba(15, 23, 42, 0.06)"
    };
  }

  return {
    pageBg: `radial-gradient(ellipse 90% 55% at 0% 0%, rgba(128, 163, 255, 0.16) 0%, transparent 52%),
      radial-gradient(ellipse 70% 45% at 100% 0%, rgba(31, 138, 101, 0.1) 0%, transparent 48%),
      linear-gradient(180deg, #141414 0%, ${reportDarkPalette.bg} 38%, #1a1a1a 100%)`,
    surface: reportDarkPalette.elevated,
    surfaceSolid: reportDarkPalette.elevated,
    surfaceRaised: reportDarkPalette.elevated,
    surfaceInset: "#1a1a1a",
    surfaceMuted: "#1a1a1a",
    headerGradient: reportDarkPalette.elevated,
    border: reportDarkPalette.stroke,
    borderStrong: "rgba(240,240,240,0.16)",
    text: reportDarkPalette.text,
    textSecondary: reportDarkPalette.textMuted,
    textMuted: reportDarkPalette.textMuted,
    accent: reportDarkPalette.accent,
    accentSoft: "rgba(128, 163, 255, 0.14)",
    accentGlow: "0 12px 32px rgba(128, 163, 255, 0.18)",
    tabRail: "#1a1a1a",
    tabIndicator: reportDarkPalette.accent,
    tabSelectedText: reportDarkPalette.bg,
    tabIdleText: reportDarkPalette.textMuted,
    jsonKey: reportDarkPalette.accent,
    jsonValue: "#7b64b8",
    shadowCard: "0 12px 28px rgba(0, 0, 0, 0.45)",
    shadowInset: `inset 0 0 0 1px ${reportDarkPalette.stroke}`,
    weatherPanel: `linear-gradient(180deg, ${reportDarkPalette.elevated} 0%, #1a1a1a 100%)`,
    pillBg: "rgba(128, 163, 255, 0.1)"
  };
};

export const getInlinePageChrome = () => {
  const isDark = getAppThemeMode() === "dark";
  const ui = getAppShellUi(isDark);
  const shell = buildSharedPageShellStyles(isDark);
  return { isDark, ui, shell };
};

export const createSelectableRowStyle = (ui, isDark, isSelected, extra = {}) => ({
  border: `1px solid ${
    isSelected
      ? isDark
        ? "rgba(128, 163, 255, 0.45)"
        : "rgba(59, 130, 246, 0.35)"
      : ui.border
  }`,
  borderRadius: 18,
  marginBottom: 10,
  padding: 12,
  background: isSelected ? ui.accentSoft : ui.surfaceRaised,
  boxShadow:
    isDark || !isSelected
      ? "none"
      : "0 14px 28px rgba(37, 99, 235, 0.14)",
  ...extra
});

export const buildSharedPageShellStyles = isDark => {
  const ui = getAppShellUi(isDark);
  return {
    pageShell: {
      padding: "12px 6px 28px",
      background: ui.pageBg,
      minHeight: "100vh"
    },
    cardHover: {
      borderRadius: "28px",
      overflow: "hidden",
      boxShadow: isDark ? "none" : ui.shadowCard,
      background: ui.surface,
      backdropFilter: isDark ? "none" : "blur(10px)",
      border: `1px solid ${ui.border}`
    },
    cardHeaderHover: {
      padding: "22px 22px 8px",
      background: ui.headerGradient
    },
    cardTitleDark: {
      color: ui.text,
      marginTop: "0px",
      minHeight: "auto",
      fontWeight: "700",
      marginBottom: "6px"
    },
    cardIconTitleMuted: {
      color: ui.textMuted,
      margin: "10px 0 0",
      fontWeight: "300"
    },
    sectionPanel: {
      background: ui.surfaceRaised,
      border: `1px solid ${ui.border}`,
      borderRadius: "18px",
      boxShadow: isDark ? "none" : "0 10px 24px rgba(15, 23, 42, 0.06)"
    }
  };
};
